import React, { useEffect, useMemo, useState } from 'react';
import {
  listTenants,
  listServicesForTenant,
  compareSettings,
} from '../../services/envScanApi';

const formatServiceType = (s) =>
  (s || '').split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const Compare = () => {
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantsError, setTenantsError] = useState(null);

  const [selectedTenants, setSelectedTenants] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [showSecrets, setShowSecrets] = useState(false);

  const [results, setResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState(null);

  const [search, setSearch] = useState('');
  const [showFilter, setShowFilter] = useState('all'); // all | different | missing

  // Load tenants
  useEffect(() => {
    let cancelled = false;
    setTenantsLoading(true);
    listTenants()
      .then((list) => {
        if (cancelled) return;
        setTenants(list || []);
        if (list && list.length >= 2) setSelectedTenants([list[0], list[1]]);
      })
      .catch((err) => !cancelled && setTenantsError(err.message))
      .finally(() => !cancelled && setTenantsLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Load service types as union across selected tenants
  useEffect(() => {
    if (selectedTenants.length === 0) {
      setServices([]);
      setSelectedService('');
      return;
    }
    let cancelled = false;
    Promise.all(selectedTenants.map((t) => listServicesForTenant(t).catch(() => [])))
      .then((lists) => {
        if (cancelled) return;
        const union = Array.from(new Set(lists.flat())).sort();
        setServices(union);
        if (union.length > 0 && !union.includes(selectedService)) {
          setSelectedService(union[0]);
        }
      });
    return () => { cancelled = true; };
  }, [selectedTenants, selectedService]);

  // Run comparison
  const runCompare = async () => {
    if (selectedTenants.length < 2 || !selectedService) return;
    setResultsLoading(true);
    setResultsError(null);
    setResults(null);
    try {
      const data = await compareSettings(selectedTenants, selectedService, showSecrets);
      setResults(data);
    } catch (err) {
      setResultsError(err.message || 'Compare failed');
    } finally {
      setResultsLoading(false);
    }
  };

  const matrix = useMemo(() => {
    if (!results) return null;
    // Map setting name → { [tenant]: value }
    const settingMap = {};
    const presentTenants = results.map((r) => r.tenantName);
    for (const app of results) {
      for (const s of app.settings) {
        if (!settingMap[s.name]) settingMap[s.name] = {};
        settingMap[s.name][app.tenantName] = s.value;
      }
    }
    const allNames = Object.keys(settingMap).sort();

    const rows = allNames.map((name) => {
      const values = presentTenants.map((t) => settingMap[name][t]);
      const present = values.filter((v) => v !== undefined && v !== null);
      const missing = values.some((v) => v === undefined || v === null);
      const distinct = new Set(present).size;
      const isDifferent = distinct > 1 || missing;
      return { name, perTenant: settingMap[name], missing, isDifferent };
    });

    return { presentTenants, rows };
  }, [results]);

  const filteredRows = useMemo(() => {
    if (!matrix) return [];
    let rows = matrix.rows;
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((r) => r.name.toLowerCase().includes(q));
    if (showFilter === 'different') rows = rows.filter((r) => r.isDifferent);
    if (showFilter === 'missing') rows = rows.filter((r) => r.missing);
    return rows;
  }, [matrix, search, showFilter]);

  const toggleTenant = (t) => {
    setSelectedTenants((prev) => {
      if (prev.includes(t)) return prev.filter((x) => x !== t);
      if (prev.length >= 5) return prev; // cap at 5 (matches backend cap)
      return [...prev, t];
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Compare Across Tenants</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Pick 2-5 tenants and one service type to see which settings differ between them.
        </p>
      </div>

      {tenantsError && (
        <div className="p-4 bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 rounded-lg mb-4">
          {tenantsError}
        </div>
      )}

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-5 mb-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tenants ({selectedTenants.length}/5 selected)
          </label>
          <div className="flex flex-wrap gap-2">
            {tenantsLoading && <span className="text-sm text-gray-400">Loading…</span>}
            {tenants.map((t) => (
              <label
                key={t}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer ${
                  selectedTenants.includes(t)
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800'
                } ${
                  !selectedTenants.includes(t) && selectedTenants.length >= 5
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
              >
                <input
                  type="checkbox"
                  className="w-4 h-4"
                  checked={selectedTenants.includes(t)}
                  onChange={() => toggleTenant(t)}
                  disabled={!selectedTenants.includes(t) && selectedTenants.length >= 5}
                />
                {t}
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Service Type</label>
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              disabled={services.length === 0}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
            >
              {services.length === 0 && <option>(pick tenants first)</option>}
              {services.map((s) => (
                <option key={s} value={s}>{formatServiceType(s)}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 self-end pb-2">
            <input
              type="checkbox"
              checked={showSecrets}
              onChange={(e) => setShowSecrets(e.target.checked)}
              className="w-4 h-4"
            />
            Show secrets
          </label>
          <button
            type="button"
            onClick={runCompare}
            disabled={selectedTenants.length < 2 || !selectedService || resultsLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 self-end"
          >
            {resultsLoading ? 'Comparing…' : 'Compare'}
          </button>
        </div>
      </div>

      {resultsError && (
        <div className="p-4 bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 rounded-lg mb-4">
          {resultsError}
        </div>
      )}

      {matrix && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-slate-700 flex flex-wrap items-center gap-3 justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                placeholder="Search settings…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800"
              />
              <div className="flex gap-1 p-0.5 bg-gray-100 dark:bg-slate-800 rounded-lg text-xs">
                {[
                  { k: 'all',       label: 'All' },
                  { k: 'different', label: 'Different only' },
                  { k: 'missing',   label: 'Missing only' },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    onClick={() => setShowFilter(opt.k)}
                    className={`px-2.5 py-1 rounded ${
                      showFilter === opt.k
                        ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 font-medium'
                        : 'text-gray-500 dark:text-slate-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <span className="text-xs text-gray-500 dark:text-slate-400">
              {filteredRows.length} of {matrix.rows.length} settings
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-950 sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-700 dark:text-gray-300 min-w-[200px]">
                    Setting Name
                  </th>
                  {matrix.presentTenants.map((t) => (
                    <th key={t} className="text-left px-4 py-2 font-medium text-gray-700 dark:text-gray-300 min-w-[200px]">
                      {t}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.name} className="border-t border-gray-100 dark:border-slate-800">
                    <td className="px-4 py-2 font-mono text-xs break-all align-top font-medium">
                      {row.name}
                      {row.isDifferent && (
                        <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                          diff
                        </span>
                      )}
                    </td>
                    {matrix.presentTenants.map((t) => {
                      const v = row.perTenant[t];
                      const isMissing = v === undefined || v === null;
                      return (
                        <td
                          key={t}
                          className={`px-4 py-2 font-mono text-xs break-all align-top ${
                            isMissing
                              ? 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 font-bold'
                              : ''
                          }`}
                        >
                          {isMissing ? 'MISSING' : v}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={matrix.presentTenants.length + 1} className="text-center py-6 text-gray-400 dark:text-slate-500">
                      No matching settings.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Compare;
